import { ChevronRightIcon, Pencil2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  InternalProjectUpdateAccountNotificationSettingsData,
  useGetInternalUpdateRemindersForUser,
  useUpdateInternalUpdateRemindersForUser,
} from "src/api/settings/account";
import { Button } from "src/components/ui/button";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { convertTimeTo12HourFormat } from "../../space-settings/space-project-settings";

type Props = {};

export default function AccountNotifications({}: Props) {
  return (
    <div className="flex h-screen flex-col bg-gray-50/40">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings/account`}>Account</Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">Notifications</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-2xl pt-20">
          <div className="space-y-1 border-b-[0.5px] border-gray-300 pb-6">
            <div className="text-xl font-medium">Notifications</div>
          </div>
          <InternalProjectUpdateNotifications />
        </div>
      </div>
    </div>
  );
}

type Day = "monday" | "tuesday" | "wednesday" | "thursday" | "friday";

function InternalProjectUpdateNotifications() {
  const { data: internalUpdateReminders } =
    useGetInternalUpdateRemindersForUser();
  console.log("internalUpdateReminders", internalUpdateReminders);
  return (
    <div className="space-y-3.5 pt-8">
      <div className="space-y-0.5">
        <div className="font-[450] text-gray-800">Internal Project Updates</div>
        <div className="text-sm font-light text-gray-500">
          Pick the times you would like to be notified about project updates via our Slack App. We
          will notify you only if you have any updates that are overdue or due
          in the next 24 hours.
        </div>
      </div>
      <div className="relative rounded-md border-[0.5px] border-gray-300 bg-white p-8 shadow-sm">
        {/* <Button variant="secondary" size={"sm"} className="h-7 w-7 p-0 absolute bottom-6 right-6">
        <Pencil2Icon viewBox="0 0 16 16" className="h-3.5 w-3.5" />
      </Button> */}
        {!!internalUpdateReminders ? (
          <>
            <Days notificationDays={internalUpdateReminders.days} />
            <Time notificationDays={internalUpdateReminders.days} />
          </>
        ) : (
          <Loading className="mx-auto" />
        )}
      </div>
    </div>
  );
}

function getTimezoneAbbreviation(timezone: string) {
  switch (timezone) {
    case "America/New_York":
      return "EST";
    case "America/Los_Angeles":
      return "PST";
    case "America/Chicago":
      return "CST";
    case "America/Denver":
      return "MST";
    case "Asia/Kolkata":
      return "IST";
    default:
      return "";
  }
}

function Time({
  notificationDays,
}: {
  notificationDays: InternalProjectUpdateAccountNotificationSettingsData["days"];
}) {
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [time, setTime] = useState(notificationDays.monday.time);
  const [timezone, setTimeZone] = useState(notificationDays.monday.timezone);
  const updateInternalUpdateRemindersForUserMutation =
    useUpdateInternalUpdateRemindersForUser();

  useEffect(() => {
    setTime(notificationDays.monday.time);
    setTimeZone(notificationDays.monday.timezone);
  }, [notificationDays]);

  useEffect(() => {
    if (updateInternalUpdateRemindersForUserMutation.isSuccess) {
      setIsEditingTime(false);
    }
  }, [updateInternalUpdateRemindersForUserMutation.isSuccess]);

  return (
    <div className="flex items-center gap-6">
      <div className="w-32 text-sm font-medium text-gray-800">Time</div>
      <div className="flex h-14 flex-1 items-center justify-between gap-2 rounded-md bg-gray-100/60 px-4">
        {!isEditingTime ? (
          <>
            <div className="flex items-center gap-1">
              <div className="text-[13px]">
                {convertTimeTo12HourFormat(time)}
              </div>
              <div className="text-[13px]">
                {getTimezoneAbbreviation(timezone)}
              </div>
            </div>
            <Button
              variant="secondary"
              size={"sm"}
              className="h-7 w-7 p-0"
              onClick={() => setIsEditingTime(true)}
            >
              <Pencil2Icon viewBox="0 0 16 16" className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger className="h-7 w-fit bg-white px-1.5 pl-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="8:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">8:00</div>{" "}
                      <div className="text-xs">AM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="9:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">9:00</div>{" "}
                      <div className="text-xs">AM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="10:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">10:00</div>{" "}
                      <div className="text-xs">AM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="11:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">11:00</div>{" "}
                      <div className="text-xs">AM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="12:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">12:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="13:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">1:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="14:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">2:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="15:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">3:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="16:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">4:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="17:00">
                    <div className="flex items-center gap-1">
                      <div className="w-9 text-right">5:00</div>{" "}
                      <div className="text-xs">PM</div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select value={timezone} onValueChange={setTimeZone}>
                <SelectTrigger className="h-7 w-fit bg-white px-1.5 pl-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">EST</SelectItem>
                  <SelectItem value="America/Los_Angeles">PST</SelectItem>
                  <SelectItem value="America/Chicago">CST</SelectItem>
                  <SelectItem value="America/Denver">MST</SelectItem>
                  <SelectItem value="Asia/Kolkata">IST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="secondary"
                size={"sm"}
                onClick={() => {
                  setIsEditingTime(false);
                  setTime(notificationDays.monday.time);
                  setTimeZone(notificationDays.monday.timezone);
                }}
              >
                Cancel
              </Button>
              <Button
                size={"sm"}
                disabled={
                  updateInternalUpdateRemindersForUserMutation.isLoading ||
                  (time === notificationDays.monday.time &&
                    timezone === notificationDays.monday.timezone)
                }
                onClick={() => {
                  updateInternalUpdateRemindersForUserMutation.mutate({
                    days: {
                      monday: {
                        isEnabled: notificationDays.monday.isEnabled,
                        time,
                        timezone,
                      },
                      tuesday: {
                        isEnabled: notificationDays.tuesday.isEnabled,
                        time,
                        timezone,
                      },
                      wednesday: {
                        isEnabled: notificationDays.wednesday.isEnabled,
                        time,
                        timezone,
                      },
                      thursday: {
                        isEnabled: notificationDays.thursday.isEnabled,
                        time,
                        timezone,
                      },
                      friday: {
                        isEnabled: notificationDays.friday.isEnabled,
                        time,
                        timezone,
                      },
                      saturday: {
                        isEnabled: notificationDays.saturday.isEnabled,
                        time,
                        timezone,
                      },
                      sunday: {
                        isEnabled: notificationDays.sunday.isEnabled,
                        time,
                        timezone,
                      },
                    },
                  });
                }}
              >
                {updateInternalUpdateRemindersForUserMutation.isLoading
                  ? "Saving..."
                  : "Save"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Days({
  notificationDays,
}: {
  notificationDays: InternalProjectUpdateAccountNotificationSettingsData["days"];
}) {
  const [isEditingDays, setIsEditingDays] = useState(false);
  const [days, setDays] =
    useState<InternalProjectUpdateAccountNotificationSettingsData["days"]>(
      notificationDays
    );

  useEffect(() => {
    setDays(notificationDays);
  }, [notificationDays]);
  const updateInternalUpdateRemindersForUserMutation =
    useUpdateInternalUpdateRemindersForUser();

  const enabledDays = useMemo(() => {
    return Object.entries(notificationDays)
      .filter(([_, value]) => value.isEnabled)
      .map(([day]) => day);
  }, [notificationDays]);

  useEffect(() => {
    if (updateInternalUpdateRemindersForUserMutation.isSuccess) {
      setIsEditingDays(false);
    }
  }, [updateInternalUpdateRemindersForUserMutation.isSuccess]);

  return (
    <div className="mb-2 flex items-center gap-6">
      <div className="w-32 text-sm font-medium text-gray-800">Days</div>
      <div className="flex h-14 flex-1 items-center justify-between gap-2 rounded-md bg-gray-100/60 px-4">
        {!isEditingDays ? (
          <>
            <div className="flex gap-1">
              {enabledDays.map((day, index) => (
                <div key={day} className="text-[13px]">
                  {day.charAt(0).toUpperCase() +
                    day.slice(1, 3) +
                    (index < enabledDays.length - 1 ? ", " : "")}
                </div>
              ))}
              {enabledDays.length === 0 && (
                <div className="text-[13px] font-light text-gray-500">
                  No days currently selected
                </div>
              )}
            </div>
            <Button
              variant="secondary"
              size={"sm"}
              className="h-7 w-7 p-0"
              onClick={() => setIsEditingDays(true)}
            >
              <Pencil2Icon viewBox="0 0 16 16" className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <>
            <div className="flex gap-1">
              <DayChip
                day="Mon"
                selected={days.monday.isEnabled}
                onClick={() =>
                  setDays({
                    ...days,
                    monday: {
                      ...days.monday,
                      isEnabled: !days.monday.isEnabled,
                    },
                  })
                }
              />
              <DayChip
                day="Tue"
                selected={days.tuesday.isEnabled}
                onClick={() =>
                  setDays({
                    ...days,
                    tuesday: {
                      ...days.tuesday,
                      isEnabled: !days.tuesday.isEnabled,
                    },
                  })
                }
              />
              <DayChip
                day="Wed"
                selected={days.wednesday.isEnabled}
                onClick={() =>
                  setDays({
                    ...days,
                    wednesday: {
                      ...days.wednesday,
                      isEnabled: !days.wednesday.isEnabled,
                    },
                  })
                }
              />
              <DayChip
                day="Thu"
                selected={days.thursday.isEnabled}
                onClick={() =>
                  setDays({
                    ...days,
                    thursday: {
                      ...days.thursday,
                      isEnabled: !days.thursday.isEnabled,
                    },
                  })
                }
              />
              <DayChip
                day="Fri"
                selected={days.friday.isEnabled}
                onClick={() =>
                  setDays({
                    ...days,
                    friday: {
                      ...days.friday,
                      isEnabled: !days.friday.isEnabled,
                    },
                  })
                }
              />
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="secondary"
                size={"sm"}
                onClick={() => {
                  setIsEditingDays(false);
                  setDays(notificationDays);
                }}
              >
                Cancel
              </Button>
              <Button
                size={"sm"}
                disabled={
                  updateInternalUpdateRemindersForUserMutation.isLoading ||
                  (days.monday.isEnabled ===
                    notificationDays.monday.isEnabled &&
                    days.tuesday.isEnabled ===
                      notificationDays.tuesday.isEnabled &&
                    days.wednesday.isEnabled ===
                      notificationDays.wednesday.isEnabled &&
                    days.thursday.isEnabled ===
                      notificationDays.thursday.isEnabled &&
                    days.friday.isEnabled ===
                      notificationDays.friday.isEnabled &&
                    days.saturday.isEnabled ===
                      notificationDays.saturday.isEnabled &&
                    days.sunday.isEnabled === notificationDays.sunday.isEnabled)
                }
                onClick={() => {
                  updateInternalUpdateRemindersForUserMutation.mutate({
                    days,
                  });
                }}
              >
                {updateInternalUpdateRemindersForUserMutation.isLoading
                  ? "Saving..."
                  : "Save"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DayChip({
  day,
  selected,
  onClick,
}: {
  day: string;
  selected: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={clsx(
        "inline-flex h-6 items-center justify-center rounded-md border bg-white p-2 px-1.5 text-[13px] shadow-sm transition-colors",
        selected
          ? "border-primary/80 text-primary-600"
          : "border-gray-200 text-gray-500",
        onClick ? "cursor-pointer hover:bg-gray-50" : "cursor-default"
      )}
      onClick={onClick}
    >
      {day}
    </div>
  );
}
